<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    type CustomerCase as Case,
    type CustomerEntity,
    type CustomerEntityWithRelationship as EntityWithRelationship,
    EntityTypeEnum,
    RelationshipTypeEnum
  } from '@/types/generated'
  import LegacyEntityForm from './LegacyEntityForm.vue'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { formatEntityName as formatEntityNameUtil } from '@/utils/entityFormatting'
  import { UserPlus } from 'lucide-vue-next'

  const { t } = useTranslation({
    en: {
      opponent: 'Opponent',
      addOpponent: 'Add Opponent',
      delete: 'Delete',
      deleteOpponent: 'Delete Opponent',
      confirmDelete: 'Confirm deletion',
      confirmDeleteMessage:
        'Are you sure you want to delete this opponent? This action cannot be undone.',
      cancel: 'Cancel',
      deleteError: 'Error',
      deleteErrorMessage: 'An error occurred while deleting the opponent. Please try again.',
      addError: 'Error',
      addErrorMessage: 'An error occurred while adding the opponent. Please try again.',
      unnamed: 'Unnamed'
    },
    de: {
      opponent: 'Gegner',
      addOpponent: 'Gegner hinzufügen',
      delete: 'Löschen',
      deleteOpponent: 'Gegner löschen',
      confirmDelete: 'Löschen bestätigen',
      confirmDeleteMessage:
        'Sind Sie sicher, dass Sie diesen Gegner löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      deleteError: 'Fehler',
      deleteErrorMessage:
        'Beim Löschen des Gegners ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      addError: 'Fehler',
      addErrorMessage:
        'Beim Hinzufügen des Gegners ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      unnamed: 'Unbenannt'
    }
  })

  const { toast } = useToast()
  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  const opponentEntities = computed(() => {
    if (!caseRecord.value?.data?.entities) return []
    return caseRecord.value.data.entities.filter((entity) =>
      entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    )
  })

  const expandedCards = ref<Set<string>>(new Set())
  const showDeleteConfirmation = ref(false)
  const opponentToDelete = ref<EntityWithRelationship | null>(null)
  const isDeletingOpponent = ref(false)

  const formatEntityName = (entity: CustomerEntity | null | undefined): string => {
    return formatEntityNameUtil(entity, t('unnamed'))
  }

  const toggleCard = (entityUuid: string) => {
    if (expandedCards.value.has(entityUuid)) {
      expandedCards.value.delete(entityUuid)
    } else {
      expandedCards.value.add(entityUuid)
    }
  }

  const confirmDeleteOpponent = (opponent: EntityWithRelationship) => {
    opponentToDelete.value = opponent
    showDeleteConfirmation.value = true
  }

  const deleteOpponent = async () => {
    if (isDeletingOpponent.value) return
    const target = opponentToDelete.value
    if (!target) return
    isDeletingOpponent.value = true
    try {
      await caseStore.deleteEntity(target)
      if (target.uuid) expandedCards.value.delete(target.uuid)
      showDeleteConfirmation.value = false
      opponentToDelete.value = null
    } catch (error) {
      console.error('Error deleting opponent:', error)
      toast({
        title: t('deleteError'),
        description: t('deleteErrorMessage'),
        variant: 'destructive'
      })
    } finally {
      isDeletingOpponent.value = false
    }
  }

  const addOpponent = async () => {
    const newEntity: CustomerEntity = {
      uuid: '',
      entity_type: EntityTypeEnum.NATURAL_PERSON,
      title: '',
      specific_title: '',
      first_name: '',
      last_name: '',
      company_name: '',
      company_legal_form: '',
      institution_name: '',
      phone: '',
      email: '',
      date_of_birth: null,
      street: '',
      street_number: '',
      address_line_2: '',
      zip: '',
      city: '',
      legal_insurance: null,
      legal_insurance_number: '',
      legal_insurance_name: '',
      is_company: 'False',
      changes: [],
      proposed_changes: []
    }

    try {
      const newEntityRelationship = await caseStore.addEntity(
        newEntity,
        RelationshipTypeEnum.ENTITIES_OPPONENT
      )

      if (newEntityRelationship?.uuid) {
        expandedCards.value.add(newEntityRelationship.uuid)
      }
    } catch (error) {
      console.error('Error adding opponent:', error)
      toast({
        title: t('addError'),
        description: t('addErrorMessage'),
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <div class="relative">
    <!-- Opponent Cards -->
    <template
      v-for="(opponent, index) in opponentEntities"
      :key="opponent.uuid"
    >
      <div
        v-if="opponent.uuid"
        :class="{ 'mt-3': index > 0 }"
      >
        <div
          class="card"
          :data-cy="`opponentCard-${index}`"
        >
          <h3
            :id="`heading-opponent-${index}`"
            class="accordion-button mb-0 h-16 bg-white"
            :class="{ collapsed: !expandedCards.has(opponent.uuid) }"
            :data-cy="`opponentCardHeader-${index}`"
            @click="toggleCard(opponent.uuid)"
          >
            <div class="flex w-full items-center">
              <div class="flex flex-col">
                <span class="text-base font-medium text-neutral-800">{{ t('opponent') }}</span>
                <span
                  v-if="!expandedCards.has(opponent.uuid)"
                  class="max-w-64 truncate text-sm font-medium text-neutral-500"
                >
                  {{ formatEntityName(opponent.entity) }}
                </span>
              </div>
            </div>
          </h3>

          <div
            v-if="expandedCards.has(opponent.uuid)"
            :id="`opponentCard-${index}`"
            :data-cy="`opponentCardBody-${index}`"
            class="card-body"
            :aria-labelledby="`heading-opponent-${index}`"
          >
            <LegacyEntityForm :entity-relationship-uuid="opponent.uuid" />
            <button
              class="pt-1.5 text-sm font-medium text-red-600 underline hover:text-red-700"
              :data-cy="`deleteOpponent-${index}`"
              @click="confirmDeleteOpponent(opponent)"
            >
              {{ t('deleteOpponent') }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Add Opponent Button -->
    <div class="mt-3">
      <Button
        variant="secondary"
        class="w-full"
        data-cy="addOpponentButton"
        @click="addOpponent"
      >
        <UserPlus class="mr-2 h-5 w-5" />
        {{ t('addOpponent') }}
      </Button>
    </div>

    <!-- Delete Confirmation Modal -->
    <AlertDialog
      :open="showDeleteConfirmation"
      @update:open="(val) => (showDeleteConfirmation = val)"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('confirmDelete') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('confirmDeleteMessage') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            @click="
              () => {
                showDeleteConfirmation = false
                opponentToDelete = null
              }
            "
          >
            {{ t('cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 hover:bg-red-700"
            :disabled="isDeletingOpponent"
            @click="deleteOpponent"
          >
            {{ t('delete') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
